import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@mui/material";
import { Button, Card, LoadingSpinner, ErrorMessage, DecimalTextField } from "@/components/ui";
import { ZoneMapPicker } from "@/components/zones/ZoneMapPicker";
import { zoneService, CreateZoneData } from "@/services/zone.service";
import { useToastStore } from "@/store/toastStore";
import { circleToPolygon, readBoundary, type ZoneBoundaryPoint } from "@/utils/zoneBoundary";

const boundaryPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

const zoneSchema = z.object({
  zoneNumber: z.number().int().min(1, "Le numéro de zone doit être supérieur à 0"),
  name: z.string().min(1, "Le nom est obligatoire").min(2, "Le nom doit contenir au moins 2 caractères"),
  description: z.string().optional(),
  geographicSector: z.string().optional(),
  deliveryOrder: z.number().int().min(0, "L'ordre de livraison doit être supérieur ou égal à 0"),
  deliveryCost: z.number().min(0, "Le coût de livraison doit être positif ou nul").optional().nullable(),
  boundary: z
    .array(boundaryPointSchema)
    .min(3, "Tracez au moins 3 points sur la carte pour définir le périmètre")
    .max(500, "Le périmètre comporte trop de points"),
  isActive: z.boolean().default(true),
});

type ZoneFormData = z.infer<typeof zoneSchema>;

export function ZoneFormPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = !!id;
  const [legacyRadiusHint, setLegacyRadiusHint] = useState(false);

  const { data: zone, isLoading: zoneLoading, error: zoneError } = useQuery({
    queryKey: ["zones", id],
    queryFn: () => zoneService.getById(id!),
    enabled: isEditing,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<ZoneFormData>({
    resolver: zodResolver(zoneSchema),
    defaultValues: {
      zoneNumber: 1,
      name: "",
      description: "",
      geographicSector: "",
      deliveryOrder: 1,
      deliveryCost: undefined as number | undefined,
      boundary: [] as ZoneBoundaryPoint[],
      isActive: true,
    },
  });

  useEffect(() => {
    if (!zone) return;

    const savedBoundary = readBoundary(zone.boundary);
    let boundary: ZoneBoundaryPoint[] = [];
    let fromRadius = false;
    if (savedBoundary) {
      boundary = savedBoundary;
    } else if (
      zone.centerLatitude != null &&
      zone.centerLongitude != null &&
      zone.radiusKm != null &&
      zone.radiusKm > 0
    ) {
      fromRadius = true;
      boundary = circleToPolygon(zone.centerLatitude, zone.centerLongitude, zone.radiusKm);
    }

    setLegacyRadiusHint(fromRadius);
    reset({
      zoneNumber: zone.zoneNumber,
      name: zone.name,
      description: zone.description || "",
      geographicSector: zone.geographicSector || "",
      deliveryOrder: zone.deliveryOrder,
      deliveryCost: zone.deliveryCost ?? undefined,
      boundary,
      isActive: zone.isActive,
    });
  }, [zone, reset]);

  const { success, error: showError } = useToastStore();

  const createMutation = useMutation({
    mutationFn: (data: CreateZoneData) => zoneService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
      success("Zone créée avec succès");
      navigate("/zones");
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      showError(err?.response?.data?.message || "Erreur lors de la création de la zone");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateZoneData> }) =>
      zoneService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones"] });
      queryClient.invalidateQueries({ queryKey: ["zones", id] });
      success("Zone modifiée avec succès");
      navigate("/zones");
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      showError(err?.response?.data?.message || "Erreur lors de la modification de la zone");
    },
  });

  const onSubmit = (data: ZoneFormData) => {
    const zoneData: CreateZoneData = {
      zoneNumber: data.zoneNumber,
      name: data.name,
      description: data.description || undefined,
      geographicSector: data.geographicSector || undefined,
      deliveryOrder: data.deliveryOrder,
      deliveryCost: data.deliveryCost ?? undefined,
      boundary: data.boundary,
      isActive: data.isActive,
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: zoneData });
    } else {
      createMutation.mutate(zoneData);
    }
  };

  const boundary = watch("boundary");

  if (isEditing && zoneLoading) {
    return <LoadingSpinner message="Chargement de la zone..." />;
  }

  if (isEditing && zoneError) {
    return <ErrorMessage message="Erreur lors du chargement de la zone." />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
        {isEditing ? "Modifier la zone" : "Nouvelle zone de livraison"}
      </Typography>

      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {(createMutation.error || updateMutation.error) && (
            <ErrorMessage message="Une erreur est survenue lors de la sauvegarde." />
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="zoneNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Numéro de zone"
                    fullWidth
                    type="number"
                    inputProps={{ min: 1 }}
                    error={!!errors.zoneNumber}
                    helperText={errors.zoneNumber?.message || "Numéro unique identifiant la zone"}
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 1)}
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="deliveryOrder"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ordre de livraison"
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    error={!!errors.deliveryOrder}
                    helperText={errors.deliveryOrder?.message}
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom de la zone"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="geographicSector"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Secteur géographique (info)"
                    fullWidth
                    placeholder="Ex: Cayenne Centre"
                    helperText="Libellé informatif. L'affectation des adresses suit le périmètre dessiné."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="deliveryCost"
                control={control}
                render={({ field }) => (
                  <DecimalTextField
                    {...field}
                    label="Forfait livraison (€) / commande"
                    fullWidth
                    value={field.value}
                    onChange={field.onChange}
                    optional
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Un seul forfait par commande livrée dans cette zone"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Périmètre de livraison
              </Typography>
              {legacyRadiusHint ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Le périmètre affiché reprend l&apos;ancien rayon. Déplacez les sommets pour suivre
                  les limites réelles, ou effacez le tracé pour dessiner à la main.
                </Alert>
              ) : null}
              <ZoneMapPicker
                boundary={boundary}
                onChange={(next) => {
                  setValue("boundary", next, { shouldValidate: true, shouldDirty: true });
                }}
                onClear={() => setLegacyRadiusHint(false)}
                height={520}
              />
              {errors.boundary ? (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  {errors.boundary.message}
                </Typography>
              ) : null}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Zone active"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
            <Button variant="text" onClick={() => navigate("/zones")} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
            >
              {isEditing ? "Enregistrer les modifications" : "Créer la zone"}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
